import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { AssetCategoryFormModal } from '../components/AssetCategoryFormModal';
import { AssetDetailModal } from '../components/AssetDetailModal';
import { AssetFilterModal, EMPTY_FILTERS, type AssetFilters } from '../components/AssetFilterModal';
import { AssetFormModal } from '../components/AssetFormModal';
import { AssetImportModal } from '../components/AssetImportModal';
import { DepreciationVoucherModal } from '../components/DepreciationVoucherModal';
import { useAuth } from '../context/AuthContext';
import { assetsApi, type Asset, type AssetCategory, type AssetsSummary } from '../lib/api/assets';
import { exportSheet, fmtDateISO } from '../lib/export-generic';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

type Tab = 'list' | 'catalog';

const PAGE_SIZE = 10;

// Icon + màu theo mã loại TS (khớp với prototype).
interface IconStyle { bg: string; fg: string; path: React.ReactNode }

const CATEGORY_ICON: Record<string, IconStyle> = {
  MT: { bg: '#dbeafe', fg: '#2563EB', path: <><rect width="20" height="12" x="2" y="6" rx="2" /><path d="M2 20h20" /><path d="m17 6-1.5-3h-7L7 6" /></> },
  DT: { bg: '#fef3c7', fg: '#a16207', path: <><rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" /></> },
  SV: { bg: '#f5f3ff', fg: '#7c3aed', path: <><rect width="20" height="8" x="2" y="2" rx="2" /><rect width="20" height="8" x="2" y="14" rx="2" /><line x1="6" x2="6.01" y1="6" y2="6" /><line x1="6" x2="6.01" y1="18" y2="18" /></> },
  OT: { bg: '#fee2e2', fg: '#dc2626', path: <><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" /></> },
  VP: { bg: '#e0f2fe', fg: '#0369a1', path: <><rect width="20" height="14" x="2" y="3" rx="2" ry="2" /><line x1="8" x2="16" y1="21" y2="21" /><line x1="12" x2="12" y1="17" y2="21" /></> },
  NT: { bg: '#fef2f2', fg: '#b45309', path: <><path d="M4 18v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" /><path d="M4 22h16" /><path d="M8 12V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v6" /></> },
  BQ: { bg: '#f5f3ff', fg: '#7c3aed', path: <><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></> },
  TH: { bg: '#f0fdf4', fg: '#15803d', path: <><path d="M3 21h18" /><path d="M5 21V7l7-5 7 5v14" /><path d="M9 21v-6h6v6" /></> },
};

function getIcon(code: string): IconStyle {
  return CATEGORY_ICON[code] ?? CATEGORY_ICON.MT;
}

const STATUS_META: Record<Asset['status'], { label: string; bg: string; fg: string; dot: string }> = {
  in_use: { label: 'Đang dùng', bg: '#dcfce7', fg: '#15803d', dot: '#22c55e' },
  depleted: { label: 'Hết KH', bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' },
  maintenance: { label: 'Bảo trì', bg: '#fef3c7', fg: '#a16207', dot: '#f59e0b' },
  disposed: { label: 'Đã thanh lý', bg: '#fee2e2', fg: '#b91c1c', dot: '#dc2626' },
};

// KPI card top-bar gradient theo prototype.
const KPI_GRADIENT = {
  blue: 'linear-gradient(to right,#2563EB,#3b82f6)',
  gray: 'linear-gradient(to right,#64748b,#94a3b8)',
  green: 'linear-gradient(to right,#16a34a,#22c55e)',
  amber: 'linear-gradient(to right,#f59e0b,#fbbf24)',
  indigo: 'linear-gradient(to right,#6366f1,#818cf8)',
} as const;

export function AssetsPage() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [tab, setTab] = useState<Tab>('list');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [summary, setSummary] = useState<AssetsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [catFormOpen, setCatFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AssetCategory | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<AssetFilters>(EMPTY_FILTERS);
  const [importOpen, setImportOpen] = useState(false);
  const [pktKhOpen, setPktKhOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const canWrite = user?.role === 'KTT' || user?.role === 'NV_KT';
  const canDelete = user?.role === 'KTT';

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [a, c, s] = await Promise.all([
        assetsApi.list(), assetsApi.listCategories(), assetsApi.summary(),
      ]);
      setAssets(a.items); setCategories(c.items); setSummary(s);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Đóng kebab menu khi click ngoài.
  useEffect(() => {
    if (!menuOpenFor) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenFor(null);
      }
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [menuOpenFor]);

  const departmentOptions = useMemo(() => {
    const s = new Set<string>();
    for (const a of assets) if (a.department) s.add(a.department);
    return Array.from(s).sort();
  }, [assets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (q) {
        const hit =
          a.code.toLowerCase().includes(q) ||
          a.name.toLowerCase().includes(q) ||
          (a.userName ?? '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (filters.statuses.length && !filters.statuses.includes(a.status)) return false;
      if (filters.categoryCodes.length && !filters.categoryCodes.includes(a.categoryCode)) return false;
      if (filters.departments.length && !(a.department && filters.departments.includes(a.department))) return false;
      return true;
    });
  }, [assets, search, filters]);

  const filterChipsCount = filters.statuses.length + filters.categoryCodes.length + filters.departments.length;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, filters]);

  const handleDelete = async (a: Asset) => {
    setMenuOpenFor(null);
    if (!window.confirm(`Xoá TS ${a.code} - ${a.name}?`)) return;
    try {
      await assetsApi.remove(a.id);
      showToast(`Đã xoá ${a.code}`);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <AppShell title="Tài sản cố định" subtitle="Quản lý TSCĐ · Khấu hao · Danh mục loại (theo TT45/2013/TT-BTC)">
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {/* Tab bar — pill group với icon */}
      <div style={tabBarStyle}>
        <button type="button" style={tabBtnStyle(tab === 'list')} onClick={() => setTab('list')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" x2="21" y1="6" y2="6" /><line x1="8" x2="21" y1="12" y2="12" /><line x1="8" x2="21" y1="18" y2="18" /><line x1="3" x2="3.01" y1="6" y2="6" /><line x1="3" x2="3.01" y1="12" y2="12" /><line x1="3" x2="3.01" y1="18" y2="18" /></svg>
          Danh sách tài sản ({filtered.length})
        </button>
        <button type="button" style={tabBtnStyle(tab === 'catalog')} onClick={() => setTab('catalog')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /></svg>
          Danh mục loại ({categories.length})
        </button>
      </div>

      {tab === 'list' && (
        <>
          {/* KPI grid với gradient top-bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginTop: 16 }}>
            <KpiCard gradient={KPI_GRADIENT.blue} label="Tổng TS" value={summary?.count ?? '—'} unit="món" labelColor="#667085" valueColor="#172033" />
            <KpiCard gradient={KPI_GRADIENT.gray} label="Nguyên giá" value={summary ? formatVND(summary.totalCost) : '—'} labelColor="#667085" valueColor="#172033" />
            <KpiCard gradient={KPI_GRADIENT.green} label="Giá trị còn lại" value={summary ? formatVND(summary.totalRemaining) : '—'} labelColor="#15803d" valueColor="#15803d" />
            <KpiCard gradient={KPI_GRADIENT.amber} label="KH tháng này" value={formatVND(18_000_000)} labelColor="#a16207" valueColor="#a16207" />
            <KpiCard gradient={KPI_GRADIENT.indigo} label="Hết khấu hao" value={summary?.depletedCount ?? '—'} unit="món" labelColor="#4f46e5" valueColor="#4f46e5" />
          </div>

          {/* Main card */}
          <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e4e7ec', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(15,23,42,.04)' }}>
            {/* Toolbar: search + Bộ lọc | PKT KH + Excel + Import + Thêm */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 340 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                  <input placeholder="Tìm mã TS, tên, người dùng..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: '100%', height: 38, padding: '0 12px 0 34px', border: '1px solid #e4e7ec', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <button type="button" onClick={() => setFilterOpen(true)} style={toolbarBtnLight}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="21" x2="14" y1="4" y2="4" /><line x1="10" x2="3" y1="4" y2="4" />
                    <line x1="21" x2="12" y1="12" y2="12" /><line x1="8" x2="3" y1="12" y2="12" />
                    <line x1="21" x2="16" y1="20" y2="20" /><line x1="12" x2="3" y1="20" y2="20" />
                    <line x1="14" x2="14" y1="2" y2="6" /><line x1="8" x2="8" y1="10" y2="14" /><line x1="16" x2="16" y1="18" y2="22" />
                  </svg>
                  Bộ lọc
                  {filterChipsCount > 0 && (
                    <span style={{ padding: '1px 7px', background: '#2563EB', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 9999 }}>{filterChipsCount}</span>
                  )}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setPktKhOpen(true)} style={toolbarBtnPrimarySoft}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M9 15h6M9 11h4" /></svg>
                  Tạo PKT KH
                </button>
                <button type="button" onClick={() => {
                  exportSheet('assets', filtered, [
                    { header: 'Mã TS', key: 'code', width: 12 },
                    { header: 'Tên tài sản', key: 'name', width: 40 },
                    { header: 'Loại', key: 'categoryCode', width: 10, format: (_, r) => r.category?.name ?? String(r.categoryCode ?? '') },
                    { header: 'Bộ phận', key: 'department', width: 16 },
                    { header: 'Người dùng', key: 'userName', width: 20 },
                    { header: 'Ngày mua', key: 'purchasedAt', width: 12, format: (v) => fmtDateISO(v) },
                    { header: 'Nguyên giá', key: 'cost', width: 18, format: (v) => Number(v) },
                    { header: '% đã KH', key: 'depreciatedPct', width: 10 },
                    { header: 'GTCL', key: 'remaining', width: 18, format: (v) => Number(v) },
                    { header: 'Trạng thái', key: 'status', width: 14 },
                  ], 'TSCĐ');
                  showToast(`Đã xuất ${filtered.length} tài sản`);
                }} style={toolbarBtnLight}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                  Xuất Excel
                </button>
                <button type="button" onClick={() => setImportOpen(true)} style={toolbarBtnLight}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                  Import Excel
                </button>
                {canWrite && (
                  <button type="button" onClick={() => setFormOpen(true)} style={toolbarBtnDark}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14M12 5v14" /></svg>
                    Thêm tài sản
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflowX: 'auto' }}>
              {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Đang tải…</div>
              ) : paged.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                  {search ? `Không có TS khớp "${search}"` : 'Chưa có TS nào'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 1200 }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr style={{ textTransform: 'uppercase', color: '#64748b', fontSize: 11, letterSpacing: 0.4, borderBottom: '1px solid #e4e7ec', textAlign: 'left', height: 42 }}>
                      <th style={{ padding: '0 14px', fontWeight: 600, width: 90 }}>Mã TS</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, minWidth: 250 }}>Tên · Loại</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, width: 100 }}>Bộ phận</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, width: 130 }}>Người dùng</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, width: 100 }}>Ngày mua</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right', width: 120 }}>Nguyên giá</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, width: 130 }}>Đã KH</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right', width: 120 }}>GTCL</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, width: 130 }}>Trạng thái</th>
                      <th style={{ padding: '0 14px', fontWeight: 600, width: 60, textAlign: 'right' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((a) => {
                      const icon = getIcon(a.categoryCode);
                      const status = STATUS_META[a.status];
                      return (
                        <tr
                          key={a.id}
                          onClick={() => setDetailId(a.id)}
                          style={{ borderTop: '1px solid #f1f5f9', height: 56, cursor: 'pointer' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                        >
                          <td style={{ padding: '0 14px', fontSize: 12, fontWeight: 700, color: '#172033' }}>{a.code}</td>
                          <td style={{ padding: '0 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 38, height: 38, borderRadius: 8, background: icon.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={icon.fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icon.path}</svg>
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#172033' }}>{a.name}</div>
                                <div style={{ fontSize: 11 }}>
                                  <span style={{ color: icon.fg, fontWeight: 500 }}>{a.category?.name ?? a.categoryCode}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '0 14px', color: '#334155', fontSize: 12 }}>{a.department ?? '—'}</td>
                          <td style={{ padding: '0 14px', fontSize: 12, color: '#334155' }}>{a.userName ?? '—'}</td>
                          <td style={{ padding: '0 14px', fontSize: 12, color: '#667085' }}>{new Date(a.purchasedAt).toLocaleDateString('vi-VN')}</td>
                          <td style={{ padding: '0 14px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#172033' }}>{formatVND(a.cost)}</td>
                          <td style={{ padding: '0 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 50, height: 6, background: '#f1f5f9', borderRadius: 9999, overflow: 'hidden' }}>
                                <div style={{ width: `${a.depreciatedPct}%`, height: '100%', background: a.depreciatedPct >= 70 ? '#f59e0b' : '#22c55e' }} />
                              </div>
                              <div style={{ fontSize: 11, color: '#667085', whiteSpace: 'nowrap' }}>{a.depreciatedPct}%</div>
                            </div>
                          </td>
                          <td style={{ padding: '0 14px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#15803d' }}>{formatVND(a.remaining)}</td>
                          <td style={{ padding: '0 14px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: status.bg, color: status.fg, fontSize: 11, fontWeight: 600, borderRadius: 9999 }}>
                              <span style={{ width: 6, height: 6, background: status.dot, borderRadius: 9999, display: 'inline-block' }} />
                              {status.label}
                            </span>
                          </td>
                          <td style={{ padding: '0 14px', textAlign: 'right', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => setMenuOpenFor(menuOpenFor === a.id ? null : a.id)}
                              title="Actions"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', width: 28, height: 28, borderRadius: 6 }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                            </button>
                            {menuOpenFor === a.id && (
                              <div ref={menuRef} style={kebabMenu}>
                                <button type="button" style={kebabItem} onClick={() => { setMenuOpenFor(null); setDetailId(a.id); }}>Xem chi tiết</button>
                                {canWrite && (
                                  <button type="button" style={kebabItem} onClick={() => { setMenuOpenFor(null); setEditingId(a.id); }}>Sửa</button>
                                )}
                                {canDelete && (
                                  <button type="button" style={{ ...kebabItem, color: '#b91c1c' }} onClick={() => handleDelete(a)}>Xoá</button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, padding: '0 4px' }}>
                <div style={{ color: '#667085', fontSize: 13 }}>
                  Hiển thị {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} của {filtered.length} tài sản
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button type="button" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={pageBtnStyle(false, currentPage <= 1)}>◀ Trước</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button key={n} type="button" onClick={() => setPage(n)} style={pageBtnStyle(n === currentPage, false)}>{n}</button>
                  ))}
                  <button type="button" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={pageBtnStyle(false, currentPage >= totalPages)}>Sau ▶</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'catalog' && (
        <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e4e7ec', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(15,23,42,.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#172033' }}>Danh mục loại tài sản</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#667085' }}>Định nghĩa loại TS · TK hạch toán · Thời gian khấu hao mặc định</p>
            </div>
            {user?.role === 'KTT' && (
              <button type="button" onClick={() => setCatFormOpen(true)} style={toolbarBtnDark}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14M12 5v14" /></svg>
                Thêm loại TS
              </button>
            )}
          </div>

          <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflowX: 'auto' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Đang tải…</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr style={{ textTransform: 'uppercase', color: '#64748b', fontSize: 11, letterSpacing: 0.4, borderBottom: '1px solid #e4e7ec', textAlign: 'left', height: 40 }}>
                    <th style={{ padding: '0 14px', fontWeight: 600, width: 80 }}>Mã</th>
                    <th style={{ padding: '0 14px', fontWeight: 600 }}>Tên loại</th>
                    <th style={{ padding: '0 14px', fontWeight: 600, width: 110 }}>TK hạch toán</th>
                    <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'center', width: 150 }}>Thời gian KH mặc định</th>
                    <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'center', width: 140 }}>Phương pháp KH</th>
                    <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'center', width: 80 }}>Số TS</th>
                    <th style={{ padding: '0 14px', fontWeight: 600, width: 60, textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => {
                    const isSw = c.account.startsWith('213');
                    return (
                      <tr key={c.code} style={{ borderTop: '1px solid #f1f5f9', height: 44 }}>
                        <td style={{ padding: '0 14px' }}>
                          <span style={{ display: 'inline-flex', padding: '2px 8px', background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 700, borderRadius: 4 }}>{c.code}</span>
                        </td>
                        <td style={{ padding: '0 14px', color: '#172033', fontWeight: 500 }}>{c.name}</td>
                        <td style={{ padding: '0 14px' }}>
                          <span style={{ display: 'inline-flex', padding: '2px 8px', background: isSw ? '#f5f3ff' : '#eff6ff', color: isSw ? '#7c3aed' : '#1d4ed8', fontSize: 11, fontWeight: 700, borderRadius: 4 }}>{c.account}</span>
                        </td>
                        <td style={{ padding: '0 14px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#172033' }}>{c.years} năm</td>
                        <td style={{ padding: '0 14px', textAlign: 'center', fontSize: 13, color: '#334155' }}>{c.method}</td>
                        <td style={{ padding: '0 14px', textAlign: 'center', fontSize: 13, color: '#334155' }}>{c.assetCount ?? 0}</td>
                        <td style={{ padding: '0 14px', textAlign: 'right' }}>
                          {user?.role === 'KTT' && (
                            <button type="button" onClick={() => setEditingCategory(c)} title="Sửa loại" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', width: 26, height: 26, borderRadius: 6 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ marginTop: 14, padding: '10px 14px', background: '#eff6ff', borderLeft: '3px solid #2563EB', borderRadius: '0 8px 8px 0', fontSize: 12, color: '#1e3a8a', lineHeight: 1.5 }}>
            <b>Ghi chú:</b> Thời gian khấu hao theo Thông tư 45/2013/TT-BTC. Có thể override cho từng TS khi thêm mới.
          </div>
        </div>
      )}

      <AssetFormModal
        open={formOpen || editingId !== null}
        editingId={editingId}
        onClose={() => { setFormOpen(false); setEditingId(null); }}
        onSaved={() => {
          const wasEdit = editingId !== null;
          setFormOpen(false); setEditingId(null);
          showToast(wasEdit ? 'Đã cập nhật tài sản' : 'Đã thêm tài sản');
          load();
        }}
        onDeleted={() => { setEditingId(null); showToast('Đã xoá tài sản'); load(); }}
      />
      <AssetCategoryFormModal
        open={catFormOpen || editingCategory !== null}
        editing={editingCategory}
        onClose={() => { setCatFormOpen(false); setEditingCategory(null); }}
        onSaved={() => {
          const wasEdit = editingCategory !== null;
          setCatFormOpen(false); setEditingCategory(null);
          showToast(wasEdit ? 'Đã cập nhật loại TS' : 'Đã thêm loại TS');
          load();
        }}
      />

      <AssetDetailModal
        open={detailId !== null}
        assetId={detailId}
        onClose={() => setDetailId(null)}
        onEdit={(id) => { setDetailId(null); setEditingId(id); }}
        onDeleted={() => { setDetailId(null); showToast('Đã xoá tài sản'); load(); }}
        canEdit={canWrite}
        canDelete={canDelete}
      />

      <AssetFilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
        initial={filters}
        categories={categories}
        departmentOptions={departmentOptions}
      />

      <AssetImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => { setImportOpen(false); showToast('Đã import TSCĐ'); load(); }}
      />

      <DepreciationVoucherModal
        open={pktKhOpen}
        onClose={() => setPktKhOpen(false)}
        onCreated={() => { showToast('Đã tạo/post PKT khấu hao'); load(); }}
      />

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}

// ==== Helper components ====

function KpiCard({ gradient, label, value, unit, labelColor, valueColor }: {
  gradient: string; label: string; value: string | number; unit?: string;
  labelColor: string; valueColor: string;
}) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: gradient }} />
      <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: 0.4, color: labelColor, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: valueColor }}>
        {value}
        {unit && <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, marginLeft: 6 }}>{unit}</span>}
      </div>
    </div>
  );
}

// ==== Inline styles ====

const tabBarStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: '#f8fafc',
  border: '1px solid #eef2f7',
  borderRadius: 9999,
  padding: 6,
};

function tabBtnStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    background: active ? '#fff' : 'transparent',
    color: active ? '#172033' : '#64748b',
    fontSize: 13,
    fontWeight: 600,
    border: active ? '1px solid #e4e7ec' : '1px solid transparent',
    borderRadius: 9999,
    cursor: 'pointer',
    boxShadow: active ? '0 1px 2px rgba(15,23,42,.05)' : 'none',
  };
}

const toolbarBtnLight: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 38,
  padding: '0 14px',
  background: '#fff',
  color: '#334155',
  fontSize: 13,
  fontWeight: 500,
  border: '1px solid #e4e7ec',
  borderRadius: 8,
  cursor: 'pointer',
};

const toolbarBtnPrimarySoft: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 38,
  padding: '0 14px',
  background: '#eff6ff',
  color: '#1d4ed8',
  fontSize: 13,
  fontWeight: 600,
  border: '1px solid #bfdbfe',
  borderRadius: 8,
  cursor: 'pointer',
};

const toolbarBtnDark: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 38,
  padding: '0 14px',
  background: '#18212f',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
};

const kebabMenu: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  right: 8,
  minWidth: 160,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  padding: 4,
  zIndex: 20,
  textAlign: 'left',
};

const kebabItem: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  color: '#334155',
  textAlign: 'left',
  borderRadius: 4,
};

function pageBtnStyle(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    padding: '6px 12px',
    background: active ? '#eff6ff' : '#fff',
    border: `1px solid ${active ? '#2563EB' : '#e4e7ec'}`,
    color: active ? '#2563EB' : disabled ? '#94a3b8' : '#334155',
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
